import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import { useTranslation } from "react-i18next";
import {
  pageFieldSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";
import {
  bob,
  cloudLift,
  motionSafe,
  rise,
  shimmerSweep,
  staggerIn,
  studioDialogBackdropSx,
  studioDialogPaperSx,
} from "../AI/studioDialogMotion";
import {
  createSkillAgentCaratula,
  deleteSkillAgentCaratula,
  duplicateSkillAgentCaratula,
  listSkillAgentCaratulas,
  updateSkillAgentCaratula,
} from "../../api/Skills/skillAgentCaratulasApi";
import { getCustomers } from "../../api/Customer/customerApi";
import { optionsFromCustomers } from "../Meetings/meetingMoodboardClientOptions";
import { emptyFreePage } from "./skillAgentMoodboardLayout";
import { assignDefaultBoards } from "./skillAgentMoodboardBoard";

const ACCENT = "#5c6bc0";

const KIND_SCOPE_OPTIONS = [
  { value: "all", label: "Todos (moodboard / recap / ficha)" },
  { value: "moodboard", label: "Moodboard" },
  { value: "resumen", label: "Recap / resumen" },
  { value: "ficha", label: "Ficha técnica" },
];

/** Nombres CRM suelen venir en MAYÚSCULAS; para el menú se ve mucho mejor en title case. */
function formatClientLabel(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const letters = s.replace(/[^A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/g, "");
  if (!letters.length) return s;
  const upperCount = letters.replace(/[^A-ZÁÉÍÓÚÜÑ]/g, "").length;
  if (upperCount / letters.length < 0.65) return s;
  return s
    .toLocaleLowerCase("es")
    .replace(
      /(^|[\s/(&\-_.])(\p{L})/gu,
      (_, sep, ch) => `${sep}${ch.toLocaleUpperCase("es")}`,
    );
}

function customerAutocompleteMenuProps({ accent, isDark, paperBg }) {
  const paperSx = {
    mt: 0.75,
    borderRadius: 2.25,
    overflow: "hidden",
    border: `1px solid ${alpha(accent, 0.22)}`,
    bgcolor: paperBg,
    boxShadow: isDark
      ? `0 18px 42px ${alpha("#000", 0.45)}, 0 0 0 1px ${alpha(accent, 0.12)}`
      : `0 18px 42px ${alpha("#0f172a", 0.16)}, 0 4px 14px ${alpha(accent, 0.12)}`,
  };
  const listboxSx = {
    py: 0.75,
    px: 0.5,
    maxHeight: 300,
    "& .MuiAutocomplete-option": {
      minHeight: 40,
      mx: 0.5,
      my: 0.2,
      px: 1.35,
      py: 0.9,
      borderRadius: 1.75,
      fontSize: 13.5,
      fontWeight: 600,
      letterSpacing: "0.01em",
      lineHeight: 1.25,
      color: "text.primary",
      transition: "background-color 120ms ease",
      '&[aria-selected="true"]': {
        bgcolor: alpha(accent, 0.16),
      },
      "&.Mui-focused, &:hover": {
        bgcolor: alpha(accent, 0.1),
      },
    },
  };
  return {
    PaperProps: { elevation: 0, sx: paperSx },
    ListboxProps: { sx: listboxSx },
    componentsProps: {
      paper: { elevation: 0, sx: paperSx },
      listbox: { sx: listboxSx },
      popper: { sx: { zIndex: (t) => t.zIndex.modal + 2 } },
    },
  };
}

const emptyForm = () => ({
  Name: "",
  CustomerId: "",
  CustomerName: "",
  KindScope: "all",
  LineLabel: "",
  OrgBrand: "STAGE",
});

/** Mini-mesas animadas: plantilla → clone → board. */
function CaratulasStage({ accent }) {
  const pages = [
    { left: 18, rot: -8, delay: "0s", label: "A" },
    { left: 38, rot: 2, delay: "0.12s", label: "V" },
    { left: 58, rot: 10, delay: "0.24s", label: "·" },
  ];
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 96,
        mt: 1.25,
        mb: 0.25,
        borderRadius: 2.25,
        overflow: "hidden",
        bgcolor: alpha(accent, 0.08),
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        animation: `${rise} 360ms cubic-bezier(0.22, 1, 0.36, 1) both`,
        ...motionSafe,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            width: "40%",
            background: `linear-gradient(90deg, transparent, ${alpha(
              "#fff",
              0.45,
            )}, transparent)`,
            animation: `${shimmerSweep} 3.6s ease-in-out infinite`,
            ...motionSafe,
          }}
        />
      </Box>
      {pages.map((p) => (
        <Box
          key={p.left}
          sx={{
            position: "absolute",
            left: `${p.left}%`,
            top: 16,
            width: 52,
            height: 64,
            borderRadius: 1.25,
            bgcolor: "#fff",
            border: "1.5px solid",
            borderColor: alpha(accent, 0.35),
            boxShadow: `0 8px 18px ${alpha(accent, 0.16)}`,
            transform: `rotate(${p.rot}deg)`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            animation: `${bob} 2.4s ease-in-out infinite`,
            animationDelay: p.delay,
            ...motionSafe,
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 8,
              borderRadius: 0.5,
              bgcolor: alpha(accent, 0.35),
            }}
          />
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: 0.75,
              bgcolor: alpha(accent, 0.14),
              border: `1px dashed ${alpha(accent, 0.45)}`,
              display: "grid",
              placeItems: "center",
              fontSize: 11,
              fontWeight: 900,
              color: accent,
            }}
          >
            {p.label}
          </Box>
          <Box
            sx={{
              width: 20,
              height: 4,
              borderRadius: 0.4,
              bgcolor: alpha(accent, 0.22),
            }}
          />
        </Box>
      ))}
      <Box
        sx={{
          position: "absolute",
          right: 14,
          bottom: 12,
          width: 36,
          height: 36,
          borderRadius: 1.5,
          bgcolor: alpha(accent, 0.16),
          border: `1px solid ${alpha(accent, 0.4)}`,
          display: "grid",
          placeItems: "center",
          color: accent,
          animation: `${cloudLift} 2.6s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <LayersOutlinedIcon sx={{ fontSize: 20 }} />
      </Box>
    </Box>
  );
}

function NuevaCaratulaStage({ accent }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 88,
        mt: 1.1,
        mb: 0.25,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: alpha(accent, 0.08),
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        animation: `${rise} 320ms ease both`,
        ...motionSafe,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: "12%",
          top: 14,
          width: 58,
          height: 60,
          borderRadius: 1.25,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.4),
          boxShadow: `0 6px 14px ${alpha(accent, 0.14)}`,
          display: "grid",
          placeItems: "center",
          animation: `${bob} 2.2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <DashboardCustomizeOutlinedIcon sx={{ fontSize: 26, color: accent }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: "42%",
          top: 22,
          width: 48,
          height: 48,
          borderRadius: 1.25,
          bgcolor: alpha(accent, 0.12),
          border: "1.5px dashed",
          borderColor: alpha(accent, 0.5),
          display: "grid",
          placeItems: "center",
          animation: `${bob} 2.5s ease-in-out infinite`,
          animationDelay: "0.15s",
          ...motionSafe,
        }}
      >
        <Typography sx={{ fontWeight: 900, fontSize: 13, color: accent }}>
          A
        </Typography>
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: "12%",
          top: 12,
          width: 54,
          height: 54,
          borderRadius: 1.5,
          bgcolor: alpha(accent, 0.16),
          border: "1.5px solid",
          borderColor: alpha(accent, 0.5),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 8px 18px ${alpha(accent, 0.18)}`,
          animation: `${cloudLift} 2.4s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <AddOutlinedIcon sx={{ fontSize: 28, color: accent }} />
      </Box>
    </Box>
  );
}

function CaratulaRow({
  row,
  busy,
  onEditLayout,
  onEditMeta,
  onDuplicate,
  onDelete,
  index,
}) {
  const accent = ACCENT;
  const scopeLabel =
    KIND_SCOPE_OPTIONS.find((o) => o.value === row.KindScope)?.label ||
    row.KindScope;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.5,
        py: 1.15,
        borderRadius: 2.25,
        border: "1px solid",
        borderColor: alpha(accent, 0.18),
        bgcolor: (th) =>
          th.palette.mode === "dark"
            ? alpha(accent, 0.08)
            : alpha(accent, 0.03),
        boxShadow: `0 4px 12px ${alpha(accent, 0.06)}`,
        transition:
          "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
        animation: `${staggerIn} 380ms cubic-bezier(0.22, 1, 0.36, 1) both`,
        animationDelay: `${Math.min(index, 8) * 45}ms`,
        ...motionSafe,
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: alpha(accent, 0.4),
          boxShadow: `0 12px 24px ${alpha(accent, 0.14)}`,
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.75,
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          bgcolor: alpha(accent, 0.14),
          color: accent,
          border: `1px solid ${alpha(accent, 0.28)}`,
        }}
      >
        <DashboardCustomizeOutlinedIcon sx={{ fontSize: 20 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography fontWeight={800} noWrap sx={{ lineHeight: 1.25 }}>
          {row.Name}
        </Typography>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ mt: 0.35 }}
        >
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.CustomerName || "Global"}
          </Typography>
          {row.LineLabel ? (
            <Chip
              size="small"
              label={row.LineLabel}
              sx={{
                height: 20,
                fontWeight: 700,
                fontSize: "0.68rem",
                bgcolor: alpha(accent, 0.12),
                color: accent,
              }}
            />
          ) : null}
          <Chip
            size="small"
            variant="outlined"
            label={scopeLabel}
            sx={{ height: 20, fontSize: "0.65rem", maxWidth: 160 }}
          />
        </Stack>
      </Box>
      <Stack direction="row" spacing={0.25}>
        <Tooltip title="Editar mesas">
          <IconButton
            size="small"
            onClick={() => onEditLayout?.(row)}
            disabled={busy}
            sx={{ color: accent }}
          >
            <DashboardCustomizeOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Metadatos">
          <IconButton
            size="small"
            onClick={() => onEditMeta?.(row)}
            disabled={busy}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Duplicar">
          <IconButton
            size="small"
            onClick={() => onDuplicate?.(row)}
            disabled={busy}
          >
            {busy ? (
              <CircularProgress size={16} />
            ) : (
              <ContentCopyOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar">
          <IconButton
            size="small"
            color="error"
            onClick={() => onDelete?.(row)}
            disabled={busy}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}

/**
 * Gestor de carátulas (plantillas PagesJson) por cliente.
 * Misma familia visual que Nuevo proyecto / ficha (blur + rise + stage).
 */
export default function SkillAgentCaratulasDialog({
  open,
  onClose,
  customers = [],
  onEditLayout,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accent = ACCENT;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [q, setQ] = useState("");
  const [filterCustomerId, setFilterCustomerId] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [localCustomers, setLocalCustomers] = useState([]);

  const customerOptions = useMemo(() => {
    const list =
      Array.isArray(customers) && customers.length
        ? customers
        : Array.isArray(localCustomers)
          ? localCustomers
          : [];
    return list
      .map((c) => ({
        id: String(c.id ?? c.Id ?? ""),
        label: formatClientLabel(c.label ?? c.name ?? c.Name ?? c.Titulo ?? ""),
      }))
      .filter((c) => c.id && c.label)
      .sort((a, b) => a.label.localeCompare(b.label, "es"));
  }, [customers, localCustomers]);

  const fieldSx = {
    ...pageFieldSx,
    "& .MuiOutlinedInput-root": {
      bgcolor: isDark ? alpha("#fff", 0.04) : alpha("#000", 0.02),
    },
  };

  const clientSelectMenuProps = useMemo(
    () =>
      customerAutocompleteMenuProps({
        accent,
        isDark,
        paperBg: isDark ? theme.palette.background.paper : "#fff",
      }),
    [accent, isDark, theme.palette.background.paper],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (filterCustomerId) params.CustomerId = Number(filterCustomerId);
      const data = await listSkillAgentCaratulas(params);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.caratulas.loadError", {
            defaultValue: "No se pudieron cargar las carátulas",
          }),
      );
    } finally {
      setLoading(false);
    }
  }, [q, filterCustomerId, t]);

  useEffect(() => {
    if (!open) return;
    load();
    if (Array.isArray(customers) && customers.length) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const raw = await getCustomers().catch(() => []);
        if (cancelled) return;
        setLocalCustomers(optionsFromCustomers(raw));
      } catch {
        if (!cancelled) setLocalCustomers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, load, customers]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError("");
    setFormOpen(true);
  };

  const openEditMeta = (row) => {
    setEditingId(row.id);
    setForm({
      Name: row.Name || "",
      CustomerId: row.CustomerId ? String(row.CustomerId) : "",
      CustomerName: row.CustomerName || "",
      KindScope: row.KindScope || "all",
      LineLabel: row.LineLabel || "",
      OrgBrand: row.OrgBrand || "STAGE",
    });
    setFormError("");
    setFormOpen(true);
  };

  const handleSaveMeta = async () => {
    const name = String(form.Name || "").trim();
    if (!name) {
      setFormError(
        t("skillAgent.caratulas.nameRequired", {
          defaultValue: "Pon un nombre a la carátula",
        }),
      );
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const payload = {
        Name: name,
        CustomerId: form.CustomerId ? Number(form.CustomerId) : null,
        KindScope: form.KindScope || "all",
        LineLabel: String(form.LineLabel || "").trim() || null,
        OrgBrand: form.OrgBrand || null,
      };
      if (editingId) {
        await updateSkillAgentCaratula(editingId, payload);
      } else {
        const created = await createSkillAgentCaratula({
          ...payload,
          PagesJson: assignDefaultBoards([emptyFreePage()]),
        });
        setFormOpen(false);
        await load();
        if (created?.id && typeof onEditLayout === "function") {
          onEditLayout(created);
        }
        return;
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      setFormError(
        err?.response?.data?.error ||
          t("skillAgent.caratulas.saveError", {
            defaultValue: "No se pudo guardar la carátula",
          }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async (row) => {
    setBusyId(row.id);
    setError("");
    try {
      await duplicateSkillAgentCaratula(row.id);
      await load();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.caratulas.duplicateError", {
            defaultValue: "No se pudo duplicar",
          }),
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (row) => {
    if (
      !window.confirm(
        t("skillAgent.caratulas.deleteConfirm", {
          defaultValue: `¿Eliminar la carátula «${row.Name}»?`,
          name: row.Name,
        }),
      )
    ) {
      return;
    }
    setBusyId(row.id);
    setError("");
    try {
      await deleteSkillAgentCaratula(row.id);
      await load();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.caratulas.deleteError", {
            defaultValue: "No se pudo eliminar",
          }),
      );
    } finally {
      setBusyId(null);
    }
  };

  const selectedCustomer =
    customerOptions.find((c) => c.id === String(form.CustomerId || "")) || null;
  const filterCustomer =
    customerOptions.find((c) => c.id === String(filterCustomerId || "")) ||
    null;

  const paperSx = {
    ...studioDialogPaperSx,
    borderRadius: 3.5,
    borderColor: alpha(accent, 0.22),
    boxShadow: `0 28px 64px ${alpha(accent, 0.2)}, 0 8px 24px rgba(15,23,42,0.16)`,
    background: isDark
      ? `linear-gradient(165deg, ${alpha(accent, 0.12)} 0%, ${theme.palette.background.paper} 42%)`
      : `linear-gradient(165deg, ${alpha(accent, 0.09)} 0%, #fff 48%)`,
    animation: `${rise} 420ms cubic-bezier(0.22, 1, 0.36, 1) both`,
    ...motionSafe,
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => !saving && onClose?.()}
        fullWidth
        maxWidth="md"
        keepMounted={false}
        transitionDuration={{ enter: 280, exit: 160 }}
        slotProps={{ backdrop: { sx: studioDialogBackdropSx } }}
        BackdropProps={{ sx: studioDialogBackdropSx }}
        PaperProps={{
          sx: { ...paperSx, minHeight: "68vh" },
        }}
      >
        <Box
          sx={{
            position: "relative",
            px: 2.75,
            pt: 2.25,
            pb: 1.5,
            overflow: "hidden",
            borderBottom: "1px solid",
            borderColor: alpha(accent, 0.14),
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: `radial-gradient(ellipse at 12% 0%, ${alpha(
                accent,
                0.22,
              )} 0%, transparent 55%)`,
              pointerEvents: "none",
            }}
          />
          <Stack
            direction="row"
            alignItems="flex-start"
            spacing={1.25}
            sx={{
              position: "relative",
              animation: `${staggerIn} 360ms cubic-bezier(0.22, 1, 0.36, 1) both`,
              ...motionSafe,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(accent, 0.16),
                color: accent,
                border: `1px solid ${alpha(accent, 0.28)}`,
                animation: `${cloudLift} 2.8s ease-in-out infinite`,
                ...motionSafe,
              }}
            >
              <DashboardCustomizeOutlinedIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 900, lineHeight: 1.2, letterSpacing: -0.2 }}
              >
                {t("skillAgent.caratulas.title", {
                  defaultValue: "Carátulas",
                })}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.35 }}
              >
                {t("skillAgent.caratulas.hint", {
                  defaultValue:
                    "Plantillas editables por cliente. Al crear moodboard, recap o ficha se clonan estas mesas.",
                })}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={openCreate}
              sx={{
                ...pagePrimaryButtonSx,
                textTransform: "none",
                fontWeight: 800,
                bgcolor: accent,
                flexShrink: 0,
                "&:hover": { bgcolor: alpha(accent, 0.88) },
              }}
            >
              {t("skillAgent.caratulas.new", { defaultValue: "Nueva" })}
            </Button>
          </Stack>
          <CaratulasStage accent={accent} />
        </Box>

        <DialogContent sx={{ pt: 2, px: 2.75 }}>
          <Stack spacing={1.5}>
            {error ? (
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            ) : null}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{
                animation: `${staggerIn} 400ms cubic-bezier(0.22, 1, 0.36, 1) both`,
                animationDelay: "60ms",
                ...motionSafe,
              }}
            >
              <TextField
                size="small"
                placeholder={t("skillAgent.caratulas.search", {
                  defaultValue: "Buscar carátula…",
                })}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <SearchOutlinedIcon
                      fontSize="small"
                      sx={{ mr: 0.75, color: "text.secondary" }}
                    />
                  ),
                }}
                sx={fieldSx}
              />
              <Autocomplete
                size="small"
                options={customerOptions}
                value={filterCustomer}
                onChange={(_, v) => setFilterCustomerId(v?.id || "")}
                getOptionLabel={(o) => o.label || ""}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                openOnFocus
                clearOnEscape
                autoHighlight
                {...clientSelectMenuProps}
                renderOption={(props, option) => {
                  const { key, ...rest } = props;
                  return (
                    <li key={key ?? option.id} {...rest}>
                      {option.label}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("skillAgent.caratulas.filterClient", {
                      defaultValue: "Cliente",
                    })}
                    placeholder="Filtrar por cliente"
                    autoComplete="off"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "off",
                    }}
                    sx={{ ...fieldSx, minWidth: 220 }}
                  />
                )}
                sx={{ minWidth: 220, flex: "1 1 220px" }}
              />
              <Button
                onClick={load}
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <RefreshOutlinedIcon fontSize="small" />
                  )
                }
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: accent,
                  flexShrink: 0,
                }}
              >
                {t("skillAgent.common.refresh", { defaultValue: "Actualizar" })}
              </Button>
            </Stack>

            {loading ? (
              <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
                <CircularProgress size={28} sx={{ color: accent }} />
              </Box>
            ) : rows.length === 0 ? (
              <Box
                sx={{
                  py: 5,
                  px: 2,
                  textAlign: "center",
                  borderRadius: 2.5,
                  border: "1px dashed",
                  borderColor: alpha(accent, 0.35),
                  bgcolor: alpha(accent, 0.04),
                  animation: `${rise} 380ms ease both`,
                  ...motionSafe,
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    mx: "auto",
                    mb: 1.25,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha(accent, 0.14),
                    color: accent,
                    animation: `${cloudLift} 2.6s ease-in-out infinite`,
                    ...motionSafe,
                  }}
                >
                  <LayersOutlinedIcon />
                </Box>
                <Typography fontWeight={800} sx={{ mb: 0.5 }}>
                  {t("skillAgent.caratulas.empty", {
                    defaultValue: "Todavía no hay carátulas",
                  })}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.75, maxWidth: 360, mx: "auto" }}
                >
                  {t("skillAgent.caratulas.emptyHint", {
                    defaultValue:
                      "Crea la primera plantilla editable. Luego la eliges al montar un moodboard, recap o ficha.",
                  })}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddOutlinedIcon />}
                  onClick={openCreate}
                  sx={{
                    ...pagePrimaryButtonSx,
                    textTransform: "none",
                    fontWeight: 800,
                    bgcolor: accent,
                    "&:hover": { bgcolor: alpha(accent, 0.88) },
                  }}
                >
                  {t("skillAgent.caratulas.new", { defaultValue: "Nueva" })}
                </Button>
              </Box>
            ) : (
              <Stack spacing={1}>
                {rows.map((row, i) => (
                  <CaratulaRow
                    key={row.id}
                    row={row}
                    index={i}
                    busy={busyId === row.id}
                    onEditLayout={onEditLayout}
                    onEditMeta={openEditMeta}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.75, pb: 2 }}>
          <Button
            onClick={onClose}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("skillAgent.common.close", { defaultValue: "Cerrar" })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={formOpen}
        onClose={() => !saving && setFormOpen(false)}
        fullWidth
        maxWidth="sm"
        keepMounted={false}
        transitionDuration={{ enter: 280, exit: 160 }}
        slotProps={{ backdrop: { sx: studioDialogBackdropSx } }}
        BackdropProps={{ sx: studioDialogBackdropSx }}
        PaperProps={{
          sx: {
            ...paperSx,
            animation: formOpen
              ? `${rise} 420ms cubic-bezier(0.22, 1, 0.36, 1) both`
              : undefined,
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            px: 2.75,
            pt: 2.25,
            pb: 1.5,
            overflow: "hidden",
            borderBottom: "1px solid",
            borderColor: alpha(accent, 0.14),
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: `radial-gradient(ellipse at 12% 0%, ${alpha(
                accent,
                0.22,
              )} 0%, transparent 55%)`,
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
              animation: `${staggerIn} 360ms cubic-bezier(0.22, 1, 0.36, 1) both`,
              ...motionSafe,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(accent, 0.16),
                color: accent,
                border: `1px solid ${alpha(accent, 0.28)}`,
                animation: `${cloudLift} 2.8s ease-in-out infinite`,
                ...motionSafe,
              }}
            >
              {editingId ? (
                <EditOutlinedIcon sx={{ fontSize: 22 }} />
              ) : (
                <AddOutlinedIcon sx={{ fontSize: 24 }} />
              )}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 900, lineHeight: 1.2, letterSpacing: -0.2 }}
              >
                {editingId
                  ? t("skillAgent.caratulas.editMeta", {
                      defaultValue: "Editar carátula",
                    })
                  : t("skillAgent.caratulas.newTitle", {
                      defaultValue: "Nueva carátula",
                    })}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.35 }}
              >
                {editingId
                  ? t("skillAgent.caratulas.editMetaLead", {
                      defaultValue:
                        "Nombre, cliente, ámbito y etiqueta de línea.",
                    })
                  : t("skillAgent.caratulas.newLead", {
                      defaultValue:
                        "Define la plantilla. Después editas las mesas con las mismas herramientas del moodboard.",
                    })}
              </Typography>
            </Box>
          </Box>
          {!editingId ? <NuevaCaratulaStage accent={accent} /> : null}
        </Box>

        <DialogContent sx={{ pt: 2, px: 2.75 }}>
          <Stack
            spacing={1.5}
            sx={{
              animation: `${staggerIn} 400ms cubic-bezier(0.22, 1, 0.36, 1) both`,
              animationDelay: "50ms",
              ...motionSafe,
            }}
          >
            {formError ? (
              <Alert severity="error" onClose={() => setFormError("")}>
                {formError}
              </Alert>
            ) : null}
            <TextField
              size="small"
              label="Nombre"
              value={form.Name}
              onChange={(e) => setForm((p) => ({ ...p, Name: e.target.value }))}
              required
              fullWidth
              autoFocus
              disabled={saving}
              sx={fieldSx}
            />
            <Autocomplete
              size="small"
              options={customerOptions}
              value={selectedCustomer}
              onChange={(_, v) =>
                setForm((p) => ({
                  ...p,
                  CustomerId: v?.id || "",
                  CustomerName: v?.label || "",
                }))
              }
              getOptionLabel={(o) => o.label || ""}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              disabled={saving}
              openOnFocus
              clearOnEscape
              autoHighlight
              {...clientSelectMenuProps}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                  <li key={key ?? option.id} {...rest}>
                    {option.label}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cliente (opcional = global)"
                  placeholder="Elige un cliente"
                  autoComplete="off"
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: "off",
                  }}
                  sx={fieldSx}
                />
              )}
            />
            <TextField
              select
              size="small"
              label="Ámbito"
              value={form.KindScope}
              onChange={(e) =>
                setForm((p) => ({ ...p, KindScope: e.target.value }))
              }
              fullWidth
              disabled={saving}
              sx={fieldSx}
            >
              {KIND_SCOPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Línea / etiqueta"
              placeholder="KIDS, MAN, URBAN…"
              value={form.LineLabel}
              onChange={(e) =>
                setForm((p) => ({ ...p, LineLabel: e.target.value }))
              }
              fullWidth
              disabled={saving}
              sx={fieldSx}
            />
            {!editingId ? (
              <Typography variant="caption" color="text.secondary">
                Tras crear se abre el editor de mesas (mismas herramientas que
                el moodboard).
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.75, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setFormOpen(false)}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveMeta}
            disabled={saving}
            sx={{
              ...pagePrimaryButtonSx,
              textTransform: "none",
              fontWeight: 800,
              bgcolor: accent,
              minWidth: 110,
              "&:hover": { bgcolor: alpha(accent, 0.88) },
            }}
          >
            {saving ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Guardar"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
